import { useEffect, useRef, useState } from "react";
import { Package } from "lucide-react";
import api from "../lib/api";

const COUNTRIES = [
  "United States", "Canada", "Mexico", "Brazil", "Germany", "France", "Spain",
  "Italy", "Netherlands", "Belgium", "Ireland", "Austria", "Switzerland", "Norway",
  "Portugal", "Greece", "Philippines", "India", "UAE", "Saudi Arabia", "Other",
];

const LS_KEY = "kv_ship_country";

/**
 * Lightweight async DHL estimator. Debounced; caches per (items,country) via state only.
 * Props:
 *   items: [{product_id, sku, qty}]
 *   compact?: boolean — smaller layout for cart drawer
 */
export default function DHLEstimator({ items, compact = false }) {
  const [country, setCountry] = useState(() => localStorage.getItem(LS_KEY) || "United States");
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const timerRef = useRef();

  useEffect(() => {
    if (!items || items.length === 0) { setQuote(null); return; }
    clearTimeout(timerRef.current);
    setError("");
    setLoading(true);
    timerRef.current = setTimeout(() => {
      api.post("/checkout/quote", { items, country })
        .then((r) => setQuote(r.data))
        .catch((e) => { setQuote(null); setError(e.response?.data?.detail || "Unable to estimate"); })
        .finally(() => setLoading(false));
    }, 350);
    return () => clearTimeout(timerRef.current);
  }, [items, country]);

  const onChange = (e) => {
    setCountry(e.target.value);
    localStorage.setItem(LS_KEY, e.target.value);
  };

  if (compact) {
    return (
      <div className="border border-white/10 p-3 bg-[#0A0E0A]" data-testid="dhl-estimator-compact">
        <div className="flex items-center gap-2 mb-2">
          <Package size={14} className="text-[#C8A97E]" />
          <span className="overline text-[10px]">DHL Express · from Borneo</span>
        </div>
        <select value={country} onChange={onChange} className="text-xs mb-2 py-1.5" data-testid="dhl-country-select">
          {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
        </select>
        <div className="text-xs" data-testid="dhl-estimate-line">
          {loading && <span className="text-[#A8A5A1]">Estimating...</span>}
          {error && <span className="text-[#FCA5A5]">{error}</span>}
          {!loading && !error && quote && (
            <span className="text-[#A8A5A1]">
              Ship <span className="text-[#F4F1ED]">${quote.shipping.toFixed(2)}</span> · {quote.shipping_info?.billable_kg}kg · 3–7 bd
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="border border-white/10 p-5 bg-[#0A0E0A]" data-testid="dhl-estimator">
      <div className="flex items-center gap-2 mb-3">
        <Package size={16} className="text-[#C8A97E]" />
        <span className="overline">Shipping — DHL Express from Borneo</span>
      </div>
      <div className="grid grid-cols-2 gap-3 items-end">
        <div>
          <label className="text-[11px] text-[#75726D] uppercase tracking-widest mb-1 block">Ship to</label>
          <select value={country} onChange={onChange} className="py-2 text-sm" data-testid="dhl-country-select">
            {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="text-right" data-testid="dhl-estimate-line">
          {loading && <span className="text-sm text-[#A8A5A1]">Estimating...</span>}
          {error && <span className="text-sm text-[#FCA5A5]">{error}</span>}
          {!loading && !error && quote && (
            <div>
              <div className="font-serif text-2xl text-[#C8A97E]">${quote.shipping.toFixed(2)}</div>
              <div className="text-[11px] text-[#75726D]">
                {quote.shipping_info?.billable_kg}kg · 3–7 business days
              </div>
            </div>
          )}
        </div>
      </div>
      <p className="text-[11px] text-[#75726D] mt-3 leading-relaxed">
        Import duties &amp; local taxes are the recipient's responsibility.
      </p>
    </div>
  );
}
